import { useContext, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import { StoreContext } from '~/store'
import { StepperContext } from '~/components/ui/ViewStepper'
import { createBooking, prepareBooking, fetchServiceAvailability } from '~/lib/api'
import { normalizeAvailability } from '~/utils/data'
import { showErrorModal } from '~/utils'
import { formatBookingRequest } from '~/utils/format'
import { track } from '~/utils/gtm'
import { markBookingPlaced } from '~/lib/returnTrip'
import i18next from '~/i18n'

/**
 * Places a NEW booking from the current Formik values and handles everything around it:
 * credit card payment through the PIQ cashier, the pending-booking poll handshake, the
 * invalid-service-token retry, GTM tracking and navigation to the 'done' view.
 *
 * Shared by ConfirmBooking (regular flow) and ReturnTripBooking (prefilled return trip
 * sheet) so the booking path exists in one place. Editing an existing booking stays in
 * ConfirmBooking.
 *
 * Callbacks:
 *   onNotAvailable      — the token retry found no AS_REQUESTED availability (regular flow goes back to 'how')
 *   onDatetimeBeforeNow — the retry was rejected with DATETIME_BEFORE_NOW (regular flow goes back to 'time')
 */
export default function usePlaceBooking({ onNotAvailable, onDatetimeBeforeNow } = {}) {
  const { next } = useContext(StepperContext)
  const { user, refreshUser, setPreferredPaymentAccount, getActiveBookings } = useContext(StoreContext)
  const { values, setValues, setFieldValue, setFieldError } = useFormikContext()

  const [isLoading, setIsLoading] = useState(false)
  const [pendingBookingId, setPendingBookingId] = useState(null)
  const [waitingForPoll, setWaitingForPoll] = useState(false)
  const timeoutRef = useRef(null)
  const creditCardBookingIdRef = useRef(null) // Set when prepareBooking returns; fallback when PAYMENT_MODAL_CLOSED never fires

  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice

  const sendToGTM = (values, bookingId) => {
    const booking = formatBookingRequest(values);
    const priceEntry = fixedPrice ? values.priceEstimate?.prices?.find((value) => value.serviceType === booking.serviceType) : null;
    const price = priceEntry?.finalPrice || false;
    const trackData = {
      price: price,
      currency: 'SEK',
      preBook: values.pickupTime ? true : false,
      pickupTime: booking.pickUpTimeStamp,
      bookingNumber: bookingId,
      carType: booking.serviceType,
      extras: booking.vehicleOptions,
      fixedPrice: fixedPrice && booking.priceToken ? true : false,
      userId: user?.userId || false,
      ...(values.originalBookingReference ? { returnTrip: true } : {})
    };
    track('confirmBooking', trackData);
  }

  const handleBookingComplete = (response) => {
    setIsLoading(false)
    setPendingBookingId(null)
    setWaitingForPoll(false)
    creditCardBookingIdRef.current = null
    // Remember the booking the user just placed so BookingDone can offer a return trip when closed
    markBookingPlaced(response.bookingId)
    // Save preferred payment - include payment method for PayInCar
    setPreferredPaymentAccount(values.account, values.isCompanyTrip, values.payment)
    // IMPORTANT: Capture all current form values before any async operations
    // This prevents form values from being cleared during navigation
    const currentValues = { ...values }
    // Use setValues to update bookingId while preserving ALL other form values
    // This ensures form values are preserved when navigating to 'done'
    setValues({
      ...currentValues,
      bookingId: response.bookingId
    }, false)
    // Navigate first, then do async operations after navigation completes
    // Use setTimeout to ensure navigation happens in the next tick
    setTimeout(() => {
      next('done')
      // Do async operations after navigation to prevent form reset
      setTimeout(() => {
        getActiveBookings()
        refreshUser()
        sendToGTM(currentValues, response.bookingId)
      }, 100)
    }, 0)
  }

  // Credit card: the booking is prepared, then paid in the PIQ cashier. The bookingId is
  // tracked in localStorage so the booking can be recovered if the modal is closed.
  const startCreditCardPayment = (response) => {
    // Track bookingId so PENDING_BOOKING_CONFIRMED can navigate even if PAYMENT_MODAL_CLOSED never fires
    creditCardBookingIdRef.current = response.bookingId
    // Store bookingID in localStorage for recovery if payment modal is closed
    const pendingBookings = JSON.parse(localStorage.getItem('pendingCreditCardBookings') || '[]')
    if (!pendingBookings.includes(response.bookingId)) {
      pendingBookings.push(response.bookingId)
      localStorage.setItem('pendingCreditCardBookings', JSON.stringify(pendingBookings))
    }

    emitCustomEvent(
      'CASHIER_PAY_BOOKING',
      {
        bookingId: response.bookingId,
        accountId: values.account.accountId,
        success: () => {
          // Remove from pending bookings on success
          const updatedPending = JSON.parse(localStorage.getItem('pendingCreditCardBookings') || '[]')
            .filter(id => id !== response.bookingId)
          localStorage.setItem('pendingCreditCardBookings', JSON.stringify(updatedPending))
          handleBookingComplete(response)
        },
        doneLoading: () => setIsLoading(false)
      }
    )
  }

  const isUnauthorized = error =>
    error?.isUnauthorized || (error?.status === 401) || (error?.response?.status === 401 && error?.response?.error === 'Unauthorized')

  const placeBooking = () => {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const bookingFunc = isCreditCard ? prepareBooking : createBooking
    console.log("Create booking request");
    setIsLoading(true)
    bookingFunc(values, user.userId).then((response) => {
      console.log("start booking");
      console.log(response)
      if (!isNaN(response?.bookingId)) {
        if (isCreditCard) {
          startCreditCardPayment(response)
        }
        else {
          handleBookingComplete(response)
        }
      }
    })
    .catch(async (error) => {
      console.log(error);
      console.log("error on booking");
      if (error?.response?.error === 'INVALID_SERVICE_TOKEN_SERVICE') {
        try {
          // The booking may have used autoValues.pickupTime (a delayed time) rather than
          // values.pickupTime. Use that same effective time for the service-availability
          // refetch so the dateTime param matches what was actually sent to /bookings.
          const effectivePickupTime = values.autoValues?.pickupTime || values.pickupTime
          const valuesForAvailability = effectivePickupTime !== values.pickupTime
            ? { ...values, pickupTime: effectivePickupTime }
            : values
          const freshAvail = normalizeAvailability(await fetchServiceAvailability(valuesForAvailability, user))
          const availKey = values.pickupTime === '' ? 'direct' : 'prebook'
          const hasAsRequested = freshAvail?.servicesByProvider
            ? freshAvail.servicesByProvider.some(p => p.availabilities?.some(a => a.availableAt === 'AS_REQUESTED'))
            : freshAvail?.availabilities?.some(a => a.availableAt === 'AS_REQUESTED')
          if (!hasAsRequested) {
            setFieldValue(`availability.${availKey}`, freshAvail, false)
            setIsLoading(false)
            onNotAvailable?.()
            return
          }
          // Update Formik so the form stays in sync with the fresh token
          setFieldValue(`availability.${availKey}`, freshAvail, false)
          // Retry with fresh availability inline (Formik update is async, can't rely on it)
          const updatedValues = { ...values, availability: { ...values.availability, [availKey]: freshAvail } }
          const retryResponse = await bookingFunc(updatedValues, user.userId)
          if (!isNaN(retryResponse?.bookingId)) {
            if (isCreditCard) {
              startCreditCardPayment(retryResponse)
            } else {
              handleBookingComplete(retryResponse)
            }
          }
        } catch (retryError) {
          if (retryError?.response?.error === 'DATETIME_BEFORE_NOW') {
            setIsLoading(false)
            onDatetimeBeforeNow?.()
            return
          }
          if (!isUnauthorized(retryError)) {
            showErrorModal(retryError?.response?.message || retryError?.message || 'Ett fel uppstod')
          }
          setIsLoading(false)
        }
        return
      }
      if (error?.response?.error === 'INVALID_PHONE_NUMBER') {
        setFieldError('phone', error?.response?.message)
      }
      else if (!isUnauthorized(error)) {
        console.error(error)
        showErrorModal(error?.response?.message || error?.message || 'Ett fel uppstod')
      }
      setIsLoading(false)
    })
  }

  // Listen for payment modal closed event
  useCustomEventListener('PAYMENT_MODAL_CLOSED', ({ bookingId }) => {
    console.log('[usePlaceBooking] Payment modal closed, bookingId:', bookingId)
    if (bookingId && !isNaN(bookingId)) {
      setPendingBookingId(bookingId)
      setWaitingForPoll(true)
      setIsLoading(true) // Show spinner - this will disable the button
      // Emit event to disable all interactions in booking module
      emitCustomEvent('BOOKING_WAITING_FOR_POLL', { waiting: true })

      // Set a timeout to enable button if no poll completes within reasonable time
      // This handles the case where user closes modal before any poll happens
      timeoutRef.current = setTimeout(() => {
        console.log('[usePlaceBooking] Timeout reached, enabling button')
        setWaitingForPoll(false)
        setIsLoading(false)
        emitCustomEvent('BOOKING_WAITING_FOR_POLL', { waiting: false })
        timeoutRef.current = null
      }, 12000)
    }
  }, [])

  // Listen for pending booking confirmation
  useCustomEventListener('PENDING_BOOKING_CONFIRMED', ({ bookingId }) => {
    // Race condition: poll fires while PIQ modal is still open (PAYMENT_MODAL_CLOSED not yet fired).
    // FormikListener handles navigation in this case; we only need to save the preference here.
    if (creditCardBookingIdRef.current === bookingId && pendingBookingId !== bookingId) {
      creditCardBookingIdRef.current = null
      setPreferredPaymentAccount(values.account, values.isCompanyTrip, values.payment)
    }
    if (pendingBookingId === bookingId) {
      creditCardBookingIdRef.current = null
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setPendingBookingId(null)
      setWaitingForPoll(false)
      setIsLoading(false)
      emitCustomEvent('BOOKING_WAITING_FOR_POLL', { waiting: false })
      markBookingPlaced(bookingId)
      setPreferredPaymentAccount(values.account, values.isCompanyTrip, values.payment)
      const currentValues = { ...values }
      setValues({
        ...currentValues,
        bookingId: bookingId
      }, false)
      setTimeout(() => {
        next('done')
        setTimeout(() => {
          getActiveBookings()
          refreshUser()
          sendToGTM(currentValues, bookingId)
        }, 100)
      }, 0)
    }
  }, [pendingBookingId, setValues, next, values, setPreferredPaymentAccount, getActiveBookings, refreshUser])

  // Listen for payment/booking failure (statusCode 190)
  useCustomEventListener('PENDING_BOOKING_FAILED', ({ bookingId }) => {
    if (pendingBookingId === bookingId || creditCardBookingIdRef.current === bookingId) {
      creditCardBookingIdRef.current = null
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setPendingBookingId(null)
      setWaitingForPoll(false)
      setIsLoading(false)
      emitCustomEvent('BOOKING_WAITING_FOR_POLL', { waiting: false })
      showErrorModal(i18next.t('error-payment'))
    }
  }, [pendingBookingId, waitingForPoll])

  // Listen for first poll result - if booking not confirmed, enable button
  useCustomEventListener('FIRST_POLL_COMPLETE', ({ bookingId }) => {
    if (pendingBookingId === bookingId && waitingForPoll) {
      // Clear timeout since poll completed
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      // First poll completed but booking not confirmed - enable button
      setWaitingForPoll(false)
      setIsLoading(false)
      // Re-enable all interactions
      emitCustomEvent('BOOKING_WAITING_FOR_POLL', { waiting: false })
      // Keep pendingBookingId so we can still navigate if it gets confirmed later
    }
  }, [pendingBookingId, waitingForPoll])

  return { placeBooking, isLoading, setIsLoading, waitingForPoll }
}
